import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { InputSwitch } from 'primereact/inputswitch';
import { Card } from 'primereact/card';
import { Badge } from 'primereact/badge';
import { Dropdown } from 'primereact/dropdown';
import { ProgressBar } from 'primereact/progressbar';
import api from '../../services/api';
import { BORDER_OPTIONS, BORDER_STATES, BORDER_CITIES_BY_STATE } from '../../constants/constants';

function cityLabel(row) {
  const cities = BORDER_CITIES_BY_STATE[row.state] || [];
  return cities.find((c) => c.code === row.city)?.name || row.city;
}

function stateLabel(row) {
  return BORDER_STATES.find((s) => s.code === row.state)?.name || row.state;
}

function borderLabel(code) {
  return BORDER_OPTIONS.find((b) => b.code === code)?.name || code;
}

function pageLabel(page) {
  if (!page) return '—';
  const title = page.h2 || page.title || page.full_slug || page.slug || page._id;
  return page.lang ? `${title} (${page.lang})` : title;
}

export default function CityGeosList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [globalFilter, setGlobalFilter] = useState('');
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selected, setSelected] = useState(null);
  const [createVisible, setCreateVisible] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newBorder, setNewBorder] = useState(null);
  const [newState, setNewState] = useState(null);
  const [newCity, setNewCity] = useState(null);
  const toast = useRef(null);
  const navigate = useNavigate();

  const stateOptions = newBorder
    ? BORDER_STATES.filter((s) => s.border === newBorder)
    : BORDER_STATES;
  const cityOptions = newState ? BORDER_CITIES_BY_STATE[newState] || [] : [];

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/city_geos');
      setItems(data || []);
    } catch (err) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load city geos' });
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newBorder || !newState || !newCity) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Validation',
        detail: 'Select border, state and city',
      });
      return;
    }
    try {
      setCreating(true);
      const { data } = await api.post('/city_geos', {
        border: newBorder,
        state: newState,
        city: newCity,
      });
      setCreateVisible(false);
      setNewBorder(null);
      setNewState(null);
      setNewCity(null);
      navigate(`/city_geos/edit/${data._id}`);
    } catch (err) {
      const status = err?.response?.status;
      const existingId = err?.response?.data?.id;
      if (status === 409 && existingId) {
        toast.current?.show({
          severity: 'info',
          summary: 'Already exists',
          detail: 'Opening the existing city geo',
        });
        setCreateVisible(false);
        navigate(`/city_geos/edit/${existingId}`);
        return;
      }
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err?.response?.data?.message || 'Failed to create city geo',
      });
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/city_geos/${selected._id}`);
      toast.current?.show({ severity: 'success', summary: 'Deleted', detail: 'City geo deleted' });
      setDeleteDialog(false);
      fetchItems();
    } catch (err) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to delete' });
    }
  };

  const toggleActive = async (row) => {
    try {
      await api.put(`/city_geos/${row._id}`, { active: !row.active });
      fetchItems();
    } catch (err) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to update' });
    }
  };

  const filtered = globalFilter.trim()
    ? items.filter((row) => {
        const q = globalFilter.toLowerCase();
        return (
          cityLabel(row).toLowerCase().includes(q) ||
          stateLabel(row).toLowerCase().includes(q) ||
          (row.border || '').toLowerCase().includes(q)
        );
      })
    : items;

  return (
    <Card>
      <div className="card p-4">
        <Toast ref={toast} />
        <div className="grid mb-4">
          <div className="col-6 flex align-items-center">
            <h2 className="m-0 main-text font-bold vertical-align-middle">
              <span>City geo</span>
              <Badge
                className="ml-2 b-4 relative"
                value={filtered.length ?? 0}
                style={{ bottom: '3px', backgroundColor: '#2020A0' }}
              />
            </h2>
          </div>
          <div className="col-6 flex justify-content-end">
            <Button
              label="Add city"
              icon="pi pi-plus"
              className="p-button-primary p-button-sm secondary-button"
              style={{ minWidth: 120 }}
              onClick={() => setCreateVisible(true)}
            />
          </div>
        </div>

        <div className="grid mb-4">
          <div className="col-12 flex gap-2 align-items-end flex-wrap">
            <div className="flex flex-column" style={{ minWidth: 250 }}>
              <label style={{ fontSize: '0.95rem' }}>
                <strong>Search</strong>
              </label>
              <InputText
                value={globalFilter}
                onChange={(e) => setGlobalFilter(e.target.value)}
                className="w-full p-button-sm"
                placeholder="Search by city or state"
                style={{ fontSize: '0.95rem' }}
              />
            </div>
            <Button
              label="Clear"
              icon="pi pi-times"
              className="p-button-text text-danger p-button-sm"
              onClick={() => setGlobalFilter('')}
            />
          </div>
        </div>

        {loading && !items.length ? (
          <div
            className="flex flex-column align-items-center justify-content-center"
            style={{ minHeight: '300px' }}
          >
            <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem', color: '#2020A0' }} />
            <div style={{ marginTop: '1rem', fontSize: '1.2rem', color: '#555' }}>
              Loading city geos...
            </div>
          </div>
        ) : (
          <DataTable
            value={filtered}
            paginator
            rows={15}
            loading={loading}
            emptyMessage="No city geos yet. Add a city to start."
            className="p-datatable-sm"
            showGridlines
            stripedRows
          >
            <Column
              header="City"
              body={(row) => <span className="font-semibold">{cityLabel(row)}</span>}
              sortable
              sortField="city"
            />
            <Column header="State" body={stateLabel} />
            <Column header="Border" body={(row) => borderLabel(row.border)} />
            <Column
              header="Base entry page"
              body={(row) => pageLabel(row.baseEntryPageId)}
            />
            <Column
              header="Maps"
              body={(row) => {
                const total = row.mapsTotal || 0;
                const complete = row.mapsComplete || 0;
                const pct = total ? Math.round((complete / total) * 100) : 0;
                return (
                  <div className="flex flex-column gap-1" style={{ minWidth: 140 }}>
                    <span className="text-sm">
                      {complete}/{total}
                    </span>
                    <ProgressBar value={pct} showValue={false} style={{ height: 8 }} />
                  </div>
                );
              }}
              style={{ width: '160px' }}
            />
            <Column
              header="Active"
              body={(row) => (
                <InputSwitch checked={row.active !== false} onChange={() => toggleActive(row)} />
              )}
              style={{ width: '100px' }}
            />
            <Column
              header="Configure"
              body={(row) => (
                <div className="flex justify-content-center">
                  <Button
                    icon="pi pi-cog"
                    className="p-button-text p-button-sm"
                    onClick={() => navigate(`/city_geos/edit/${row._id}`)}
                    tooltip="Edit city geo"
                    tooltipOptions={{ position: 'top' }}
                  />
                  <Button
                    icon="pi pi-trash"
                    className="p-button-text p-button-sm p-button-danger"
                    onClick={() => {
                      setSelected(row);
                      setDeleteDialog(true);
                    }}
                    tooltip="Delete"
                    tooltipOptions={{ position: 'top' }}
                  />
                </div>
              )}
              style={{ width: '8rem', textAlign: 'center' }}
            />
          </DataTable>
        )}

        <Dialog
          visible={createVisible}
          header="New city geo"
          modal
          style={{ width: '32rem' }}
          onHide={() => setCreateVisible(false)}
          footer={
            <div>
              <Button label="Cancel" icon="pi pi-times" text onClick={() => setCreateVisible(false)} />
              <Button
                label="Create and edit"
                icon="pi pi-check"
                className="secondary-button"
                loading={creating}
                onClick={handleCreate}
              />
            </div>
          }
        >
          <div className="flex flex-column gap-3 p-fluid">
            <div>
              <label className="font-semibold">Border</label>
              <Dropdown
                value={newBorder}
                options={BORDER_OPTIONS}
                optionLabel="name"
                optionValue="code"
                placeholder="Select border"
                onChange={(e) => {
                  setNewBorder(e.value);
                  setNewState(null);
                  setNewCity(null);
                }}
                className="w-full mt-1"
              />
            </div>
            <div>
              <label className="font-semibold">State</label>
              <Dropdown
                value={newState}
                options={stateOptions}
                optionLabel="name"
                optionValue="code"
                placeholder="Select state"
                disabled={!newBorder}
                onChange={(e) => {
                  setNewState(e.value);
                  setNewCity(null);
                }}
                className="w-full mt-1"
              />
            </div>
            <div>
              <label className="font-semibold">City</label>
              <Dropdown
                value={newCity}
                options={cityOptions}
                optionLabel="name"
                optionValue="code"
                placeholder="Select city"
                disabled={!newState}
                filter
                onChange={(e) => setNewCity(e.value)}
                className="w-full mt-1"
              />
            </div>
            <small className="text-600">
              Creates the city template. Next you will pick the base entry page and draw maps per port and transport mode.
            </small>
          </div>
        </Dialog>

        <Dialog
          visible={deleteDialog}
          header="Confirm delete"
          modal
          onHide={() => setDeleteDialog(false)}
          footer={
            <div>
              <Button label="Cancel" icon="pi pi-times" text onClick={() => setDeleteDialog(false)} />
              <Button label="Delete" icon="pi pi-trash" severity="danger" onClick={handleDelete} />
            </div>
          }
        >
          <p>
            Delete city geo for <strong>{selected ? cityLabel(selected) : ''}</strong>? This also removes its synced geofences and places.
          </p>
        </Dialog>
      </div>
    </Card>
  );
}
